import {
  JSXElementConstructor,
  ReactElement,
  cloneElement,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { withTheme } from '@mui/styles';

import { CommonLayout } from '@selfserve/react-ui';
import {
  adminPaths,
  IResponse,
  IRoutePath,
  responseTypes,
} from '@selfserve/react-utils';
import { storage, storageKeys, useCurrentRoute, useStorage } from '@selfserve/react-hooks';

import { AdminNavigation } from './admin-navigation';

import { useCatalogItemsRequest } from '../data';
import { ModalSettings, withErrorHandling } from '@selfserve/react-hocs';

const StyledAdminLayoutContainer = styled('div')(
  ({ theme }: { theme?: any }) => ({
    height: '100%',
    width: '100%',
    display: 'flex',
    marginTop: '10px',
    borderTop: `1px solid ${theme.palette.grey[400]}`,
  })
);

const StyledContentContainer = styled('div')(
  ({ theme, isUpdate }: { theme?: any; isUpdate: boolean }) => ({
    position: 'relative',
    height: '100%',
    width: 'calc(100% - 280px)',
    padding: '24px 32px 48px',
    ...(isUpdate && {
      paddingTop: 0,
      paddingLeft: '64px',
      paddingRight: '42px',
    }),
  })
);

export interface IContextData {
  catalogItems: any;
  handleGetCatalogItems: (value: object) => Promise<IResponse<any>>;
  openErrorModal: (settings?: ModalSettings) => void;
}

interface Props {
  children: ReactElement<any, string | JSXElementConstructor<any>>;
  theme: any;
}

const AdminLayout = withTheme(
  withErrorHandling<Props>(({ children, theme, openErrorModal }) => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const [, setProductFamilies] = useStorage<string[]>(storage.SESSION, storageKeys.PRODUCT_FAMILIES, []);
    const [paths, setPaths] = useState<IRoutePath[]>(adminPaths());
    const {
      data: { data: catalogItems, status } = {},
      refetch: handleGetCatalogItems,
    } = useCatalogItemsRequest();
    const isUpdate = useMemo(() => pathname.includes('/update'), [pathname]);
    const { path: currentPath } = useCurrentRoute(paths);

    useEffect(() => {
      if (String(status).startsWith(responseTypes.SUCCESS)) {
        const productFamilies = Object.keys(catalogItems || {});
        setProductFamilies(productFamilies);
        setPaths(adminPaths(productFamilies));
      } else if (status) {
        openErrorModal({ text: catalogItems?.['message'] as string });
      }
    }, [status]);

    return (
      <>
        <Helmet>
          <title>{t('titles.admin')}</title>
        </Helmet>
        <CommonLayout displayName={'Admin'}>
          <StyledAdminLayoutContainer>
            <AdminNavigation
              paths={paths}
              currentPath={currentPath as string}
            />
            <StyledContentContainer isUpdate={isUpdate}>
              {cloneElement(children, {
                context: {
                  catalogItems,
                  handleGetCatalogItems,
                  openErrorModal,
                },
              })}
            </StyledContentContainer>
          </StyledAdminLayoutContainer>
        </CommonLayout>
      </>
    );
  })
);

export { AdminLayout };
